import Features from '@dashboard/enums/features';
import Plans from '@dashboard/enums/plans';
import { getPlan } from './plans';

// Dated grandfathering events: sellers who subscribed before an event keep the
// features it moved up a tier. Add an entry per change; mirror in the API copy at
// apps/api/src/sellers/feature-access/features.js.
export const GRANDFATHER_CUTOFFS = {
  freePlanPricingRestructure: new Date('2026-07-22T00:00:00.000Z'),
};

export const getSubscribedAt = seller =>
  seller?.stripeSubscriptionStart || seller?.createdAt || undefined;

export const features = {
  [Features.VARIANT_PRICE]: {
    label: 'Variant price',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.DOWNLOADS]: {
    label: 'Downloads',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CUSTOMIZE_CONFIRMATION]: {
    label: 'Customize confirmation',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CUSTOMIZE_EMAIL_CONFIRMATION]: {
    label: 'Customize email confirmation',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.SUBSCRIPTIONS]: {
    label: 'Subscriptions',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.ZAPIER]: {
    label: 'Zapier integration',
    plans: [Plans.FREE, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.REWARDFUL]: {
    label: 'Rewardful integration',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.PAYMENT_PLAN]: {
    label: 'Payment plan',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.SUBSCRIPTION_START_DATE]: {
    label: 'Subscription start date',
    plans: [Plans.FREE, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.SETUP_FEE]: {
    label: 'Setup fee',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.DISCOUNTED_FROM_PRICE]: {
    label: 'Discounted from',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.AG_PAY]: {
    label: 'Apple Pay / Google Pay',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.MULTIPLE_PAYMENT_METHODS]: {
    label: 'Multiple payment methods',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CUSTOM_CSS]: {
    label: 'Custom CSS',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CHECKOUT_PAGE_BADGE]: {
    label: 'Checkout Page badge',
    plans: [Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.LICENSE_KEYS]: {
    label: 'licenseKeys',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.BULK_DISCOUNTS]: {
    label: 'Bulk discounts',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.PAY_WHAT_YOU_WANT_PRICING]: {
    label: 'Pay what you want pricing',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.NOTIFY_EMAIL]: {
    label: 'Add more recipients',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.WEBSITE_URL]: {
    label: 'Website URL',
    plans: [Plans.FREE, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CUSTOM_DOMAIN]: {
    label: 'Custom domain',
    plans: [Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.EMAIL_DOMAIN]: {
    label: 'Email domain',
    plans: [Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.SEATS]: {
    label: 'Team members',
    plans: [Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.MULTI_STEP_CHECKOUT]: {
    label: 'Multi-step checkout',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.REDIRECT_TO_URL]: {
    label: 'Redirect to URL',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.HIDE_FIELDS]: {
    label: 'Hide fields',
    plans: [Plans.FREE, Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.EXPORTS]: {
    label: 'Exports',
    plans: [Plans.FREE, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.DYNAMIC_PRICING]: {
    label: 'Dynamic pricing',
    plans: [Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.WEBHOOKS]: {
    label: 'Webhooks',
    plans: [Plans.FREE, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.GOOGLE_SHEETS]: {
    label: 'Google Sheets',
    plans: [Plans.FREE, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.ONE_CLICK_UPSELLS]: {
    label: 'One-click upsells',
    plans: [Plans.SCALE, Plans.UNLIMITED],
    grandfather: { plans: [Plans.LAUNCH], before: GRANDFATHER_CUTOFFS.freePlanPricingRestructure },
  },
  [Features.EMAIL_CUSTOMER]: {
    label: 'Email customer',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CUSTOM_FEES]: {
    label: 'Custom fees',
    plans: [Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.MANUAL_PAYMENTS]: {
    label: 'Manual payments',
    plans: [Plans.SCALE, Plans.UNLIMITED],
    grandfather: { plans: [Plans.LAUNCH], before: GRANDFATHER_CUTOFFS.freePlanPricingRestructure },
  },
  [Features.TRACKING_PIXELS]: {
    label: 'Tracking pixels',
    plans: [Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.OPTIONAL_EMAIL_CONFIRMATION]: {
    label: 'Optional email confirmation',
    plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH, Plans.SCALE, Plans.UNLIMITED],
  },
  [Features.CART_ABANDONMENT]: {
    label: 'Cart abandonment',
    plans: [Plans.SCALE, Plans.UNLIMITED],
    grandfather: {
      plans: [Plans.STARTER, Plans.PREMIUM, Plans.LAUNCH],
      before: GRANDFATHER_CUTOFFS.freePlanPricingRestructure,
    },
  },
};

const SEATS = {
  [Plans.FREE]: 1,
  [Plans.STARTER]: 1,
  [Plans.PREMIUM]: 1,
  [Plans.LAUNCH]: Infinity,
  [Plans.SCALE]: Infinity,
  [Plans.UNLIMITED]: Infinity,
};

export const amountOfSeats = plan => {
  return SEATS[plan];
};

export const shouldHideFeature = (feature, canUse) => {
  const featureToCheck = features[feature];

  if (!featureToCheck) return false;

  if (canUse === false && featureToCheck.hideFeature) return true;

  return false;
};

export const canUseFeature = (feature, plan, { sellerId, subscribedAt } = {}) => {
  if (!feature) return true;

  const featureToCheck = features[feature];

  if (!featureToCheck) return true;

  // if featureToCheck contain sellerIds, it should only be available for those sellers
  if (featureToCheck.sellerIds && !featureToCheck.sellerIds?.includes(sellerId)) return false;

  if (featureToCheck.sellerIds?.includes(sellerId)) return true;

  if (featureToCheck.plans.includes(plan)) return true;

  // Grandfathered sellers (subscribed before the event cutoff) keep the moved feature.
  const { grandfather } = featureToCheck;
  if (
    grandfather &&
    grandfather.plans.includes(plan) &&
    subscribedAt &&
    new Date(subscribedAt) < grandfather.before
  ) {
    return true;
  }

  return false;
};

export const getCheapestPlanForFeature = feature => {
  if (!features[feature]) return undefined;

  // get first plan in features[features] that is available
  const cheapestPlan = features[feature].plans.find(plan => getPlan(plan).available);

  if (!cheapestPlan) return undefined;

  return getPlan(cheapestPlan);
};
